// Yönetim kabuğunun gezinme ağacı.
//
// Yan menü eskiden üç uzun düz listeden oluşuyordu (yaklaşık 40 satır) ve
// aranan sayfayı bulmak için sürekli kaydırmak gerekiyordu. Artık sayfalar iş
// alanlarına göre gruplanır; gruplar açılıp kapanır ve menüde arama yapılabilir.
//
// permissions alanı olan öğeler, kullanıcının o yetkilerden en az birine sahip
// olmadığı durumda gizlenir; roles alanı olanlar yalnızca o rollere görünür.
// Yetkiler admin panelindeki "Kullanıcı Yetkilendirme" ekranından verilir.
import { hasAnyPermission } from "../utils/roleManagement";
import { LEAGUE_SETTINGS_PERMISSIONS } from "../features/leagueSettings/permissions";
import { DESKTOP_NAV_GROUP_KEYS, isDesktopApp } from "../utils/desktopApp";

export const ACCOUNT_PATH = "/yonetim/hesabim";
export const TODOS_PATH = "/yonetim/yapilacaklar";
export const TOOLS_PATH = "/yonetim/araclar";
// Muhasebe merkezi: Hesaplar ve Virman, Ön Muhasebe, Maç Muhasebesi, Türkiye Geneli.
export const ACCOUNTING_PATH = `${TOOLS_PATH}/muhasebe`;
export const accountingSectionPath = (section) => `${ACCOUNTING_PATH}?bolum=${section}`;

// Araçlar: her yönetim kullanıcısının kişisel çalışma araçları. Yetki
// gerekmez; kayıtlar yalnız sahibine görünür. Yapılacaklar Listesi de burada.
export const TOOL_ITEMS = [
  { key: "hub", name: "Araçlar Merkezi", path: TOOLS_PATH, icon: "toolbox", description: "Tüm araçlar ve bugünün özeti tek ekranda." },
  { key: "projects", name: "Projeler", path: `${TOOLS_PATH}/projeler`, icon: "project", description: "Süreç adımları ve sorumlular, soru-cevap, aranan kişiler, görüşme notları ve portföy tek yerde." },
  { key: "todos", name: "Yapılacaklar Listesi", path: TODOS_PATH, icon: "checklist", badge: "todos", description: "Görevler, hatırlatmalar, maç görevleri ve pano görünümü." },
  { key: "agenda", name: "Ajanda ve Takvim", path: `${TOOLS_PATH}/ajanda`, icon: "calendar", description: "Ay, hafta ve gün görünümü; tekrar eden etkinlikler ve hatırlatmalar." },
  { key: "notes", name: "Not Defteri", path: `${TOOLS_PATH}/not-defteri`, icon: "notebook", description: "Renkli notlar, işaretlenebilir listeler, etiket ve arşiv." },
  { key: "accounting", name: "Muhasebe", path: `${TOOLS_PATH}/muhasebe`, icon: "receipt", description: "Hesaplar ve virman, ön muhasebe, maç muhasebesi ve Türkiye geneli tek yerde." },
  { key: "sheets", name: "Tablolar", path: `${TOOLS_PATH}/tablolar`, icon: "sheet", description: "Formüllü hesap tabloları: TOPLA, EĞER, DÜŞEYARA… çoklu sayfa ve şablonlar." },
  { key: "contacts", name: "Rehber", path: `${TOOLS_PATH}/rehber`, icon: "contact", description: "Kişi ve cari kartları; tek dokunuşla ara, yaz, WhatsApp." },
  { key: "calc", name: "Hesap Araçları", path: `${TOOLS_PATH}/hesap-araclari`, icon: "calculator", description: "KDV, yüzde, iskonto, aidat bölüştürme ve tarih hesapları." },
  { key: "focus", name: "Odak Sayacı", path: `${TOOLS_PATH}/odak-sayaci`, icon: "timer", description: "Pomodoro, geri sayım ve kronometre; maç süresi için de uygun." },
];

export const NAV_GROUPS = [
  {
    key: "genel",
    label: "Genel",
    icon: "dashboard",
    items: [
      { name: "Genel Bakış", path: "/editor", icon: "dashboard" },
      // Admin merkezi yalnızca admin'e; diğer roller "/yonetim" üzerinden
      // kendi merkezlerine yönlenir.
      { name: "Yönetim Merkezi", path: "/yonetim/admin", icon: "grid", roles: ["admin"] },
      { name: "Yönetim Merkezi", path: "/yonetim", icon: "grid", excludeRoles: ["admin"] },
      // Mesaj Yönetimi yetkisi olmayan yönetim hesapları (ör. maç yöneticisi)
      // aynı ekranda yalnız yönetim ekibiyle yazışır ve arar.
      { name: "Ekip Sohbeti", path: "/yonetim/admin/mesajlar", icon: "message", hideWithPermissions: ["messages.view"] },
    ],
  },
  {
    key: "araclar",
    label: "Araçlar",
    icon: "toolbox",
    items: TOOL_ITEMS.map((tool) => ({ name: tool.name, path: tool.path, icon: tool.icon, ...(tool.badge ? { badge: tool.badge } : {}) })),
  },
  {
    key: "lig",
    label: "Lig Yönetimi",
    icon: "trophy",
    items: [
      { name: "Ligler", path: "/editor/ligler", icon: "trophy", permissions: ["leagues.create", "leagues.update", "leagues.delete", "leagues.archive"] },
      { name: "Sezonlar", path: "/editor/sezonlar", icon: "calendar", permissions: ["seasons.create", "seasons.update", "seasons.delete", "seasons.archive", "seasons.rule_set"] },
      // Gruplar artık Lig Ayarlamaları → Lig Statüsü bölümünde.
      { name: "Lig Statüsü", path: "/editor/lig-ayarlari?bolum=lig-statusu", icon: "layers", permissions: ["groups.view", "groups.create", "groups.update", "groups.delete"] },
      { name: "Puan Durumu", path: "/editor/puan-durumu", icon: "standings", permissions: ["standings.update"] },
      { name: "Takımlar", path: "/editor/takimlar", icon: "shield", permissions: ["teams.create", "teams.update", "teams.delete"] },
      { name: "Takım Birleştirme", path: "/editor/takim-birlestirme", icon: "layers", permissions: ["teams.merge"] },
      { name: "Oyuncular", path: "/editor/oyuncular", icon: "users", permissions: ["players.create", "players.update", "players.delete"] },
      { name: "Oyuncu Birleştirme", path: "/editor/oyuncu-birlestirme", icon: "layers", permissions: ["players.merge"] },
      // Maç oluşturma ve eski maç adresleri de "Maçlar" altında sayılır.
      { name: "Maçlar", path: "/editor/maclar", icon: "ball", permissions: ["matches.view", "matches.create", "matches.update"], aliases: ["/editor/mac-olustur", "/editor/mac-guncelle", "/editor/ist-gir", "/editor/mac-ekle"] },
      { name: "Maçları Toplu Düzenle", path: "/editor/maclar/toplu-duzenle", icon: "pen", permissions: ["matches.bulk_edit"] },
      { name: "Kura Çekimi", path: "/yonetim/kura", icon: "shuffle", permissions: ["draws.view", "draws.run"] },
    ],
  },
  {
    key: "planlama",
    label: "Saha ve Planlama",
    icon: "pin",
    items: [
      { name: "Saha ve Maç Planlama", path: "/editor/sahalar", icon: "pin", permissions: ["venues.view", "venues.manage"] },
      { name: "Takım Müsaitlik Tablosu", path: "/editor/musaitlik", icon: "clock", permissions: ["venues.view"] },
      { name: "Takım Rehberi", path: "/editor/takim-rehberi", icon: "book", permissions: ["teams.directory"] },
      { name: "Personel", path: "/yonetim/admin/personel", icon: "badge", permissions: ["staff.view"] },
      { name: "Toplu Personel Atama", path: "/yonetim/admin/personel/toplu-atama", icon: "checklist", permissions: ["staff.assign"] },
      { name: "Maç Değerlendirmeleri", path: "/yonetim/mac-degerlendirmeleri", icon: "checklist", permissions: ["evaluations.view", "evaluations.submit", "evaluations.manage"] },
    ],
  },
  {
    key: "icerik",
    label: "İçerik ve Ödüller",
    icon: "pen",
    items: [
      { name: "Haberler", path: "/editor/haberler", icon: "news", permissions: ["news.create", "news.update"] },
      { name: "Yazı Ekle", path: "/editor/yazi-ekle", icon: "pen", permissions: ["news.writings"] },
      { name: "İçerik Havuzu", path: "/icerik-havuzu", icon: "image", permissions: ["content.pool"] },
      { name: "Haftanın Enleri", path: "/yonetim/haftanin-enleri", icon: "star", permissions: ["weekly_awards.view", "weekly_awards.update"] },
      { name: "Elitlig Panorama", path: "/yonetim/panorama", icon: "tv", permissions: ["panorama.view", "panorama.broadcast"] },
      { name: "Sponsorlar", path: "/yonetim/sponsorlar", icon: "handshake", permissions: ["sponsors.view", "sponsors.manage"] },
      { name: "Junior Cup", path: "/yonetim/junior-cup", icon: "trophy", permissions: ["league_portals.view", "league_portals.manage"] },
    ],
  },
  {
    key: "finans",
    label: "Finans ve Piyasa",
    icon: "wallet",
    items: [
      // Muhasebe ve Türkiye Geneli Muhasebe, Araçlar → Muhasebe merkezine taşındı.
      { name: "Takım Finansı & FFP", path: "/yonetim/takim-finans", icon: "coins", permissions: ["team_finance.view", "team_finance.manage"] },
      { name: "Piyasa Değeri Yönetimi", path: "/yonetim/piyasa-degeri", icon: "chart", permissions: ["market_value.view", "market_value.recalculate", "market_value.settings"] },
      { name: "Piyasa Değeri Sıralaması", path: "/piyasa-degeri", icon: "trending", permissions: ["market_value.view"] },
    ],
  },
  {
    key: "kural",
    label: "Kurallar ve Disiplin",
    icon: "gavel",
    items: [
      // Kural kitapçıkları; puanlama sistemi ve kadro kuralları da kitapçıktan yönetilir.
      // Eski adresler (/editor/kurallar, /editor/kadro-kural-setleri) çalışmaya devam eder.
      { name: "Lig Ayarlamaları", path: "/editor/lig-ayarlari", icon: "standings", permissions: LEAGUE_SETTINGS_PERMISSIONS },
      { name: "Kadro Listeleri", path: "/editor/kadro-listeleri", icon: "users", permissions: ["roster_rules.requests"] },
      { name: "Kadro Onayları", path: "/editor/kadro-onaylari", icon: "check", permissions: ["roster_rules.requests"] },
      { name: "Transfer Onayları", path: "/yonetim/admin/transfer-onaylari", icon: "swap", permissions: ["transfers.view", "transfers.review"] },
      { name: "Cezalar (Disiplin)", path: "/editor/cezalar", icon: "gavel", permissions: ["penalties.view", "penalties.create"] },
      { name: "Savunma ve İtirazlar", path: "/yonetim/admin/savunmalar", icon: "message", permissions: ["penalties.review"] },
    ],
  },
  {
    key: "kullanici",
    label: "Kullanıcılar ve Sistem",
    icon: "userCog",
    items: [
      // İki ayrı ekran: "Üye Yönetimi" site üyelerini (oyuncu, takım başkanı),
      // "Yönetim Kullanıcıları" ise admin/editör gibi yetkili hesapları listeler.
      { name: "Üye Yönetimi", path: "/yonetim/admin/kullanicilar", icon: "users", permissions: ["users.view"] },
      { name: "Şifre Sıfırlama Talepleri", path: "/yonetim/admin/sifre-talepleri", icon: "lock", permissions: ["users.password_reset"] },
      { name: "Yönetim Kullanıcıları", path: "/yonetim/admin/yonetim-kullanicilari", icon: "userPlus", permissions: ["management_users.view"] },
      { name: "Kullanıcı Yetkilendirme", path: "/yonetim/admin/yetkilendirme", icon: "key", permissions: ["permissions.view", "permissions.manage"] },
      { name: "Premium Onayları", path: "/editor/premium-onaylari", icon: "crown", permissions: ["users.membership"] },
      { name: "Mesaj Yönetimi", path: "/yonetim/admin/mesajlar", icon: "mail", permissions: ["messages.view"] },
      { name: "Şehirler", path: "/yonetim/admin/sehirler", icon: "building", permissions: ["city_pages.manage"] },
      { name: "Şehir İletişim Sayfaları", path: "/yonetim/admin/iletisim", icon: "phone", permissions: ["city_pages.contacts"] },
      { name: "Ziyaretçi Analizi", path: "/yonetim/admin/ziyaretci-analizi", icon: "chart", permissions: ["analytics.view"] },
      { name: "İşlem Kayıtları", path: "/yonetim/admin/islem-kayitlari", icon: "checklist", permissions: ["activity_logs.view"] },
      { name: "Çöp Kutusu", path: "/yonetim/admin/cop-kutusu", icon: "trash", permissions: ["trash.view"] },
    ],
  },
];

export const canSeeNavItem = (user, item) =>
  (!item.roles || item.roles.includes(user?.role)) &&
  (!item.excludeRoles || !item.excludeRoles.includes(user?.role)) &&
  (!item.permissions || hasAnyPermission(user, item.permissions)) &&
  (!item.hideWithPermissions || !hasAnyPermission(user, item.hideWithPermissions));

// "ı" ve "i" aynı sayılır: eski bağlantılarda "/editor/maçlar" gibi yazımlar var.
const normalizePath = (value = "") => value.replace(/ı/g, "i").replace(/\/+$/, "") || "/";

/** Yetkiye göre süzülmüş gruplar; boş kalan grup listeden düşer. */
export function visibleNavGroups(user, query = "", { desktop = isDesktopApp() } = {}) {
  const text = query.trim().toLocaleLowerCase("tr-TR");
  // Mac masaüstü uygulaması yalnız Araçlar'ı gösterir (utils/desktopApp.js).
  const groups = desktop ? NAV_GROUPS.filter((group) => DESKTOP_NAV_GROUP_KEYS.includes(group.key)) : NAV_GROUPS;
  return groups.map((group) => {
    const items = group.items
      .filter((item) => canSeeNavItem(user, item))
      .filter((item) => !text || `${item.name} ${group.label}`.toLocaleLowerCase("tr-TR").includes(text));
    return { ...group, items };
  }).filter((group) => group.items.length > 0);
}

/**
 * Açık sayfaya en iyi uyan menü öğesi. Tam eşleşme yoksa en uzun ön ek
 * kazanır: "/editor/maclar/12/canli" sayfasında "Maçlar" seçili görünür.
 */
export function findActiveNav(pathname, groups) {
  const path = normalizePath(pathname);
  let best = null;
  for (const group of groups) {
    for (const item of group.items) {
      const target = normalizePath(item.path);
      const matches = path === target || (target !== "/editor" && target !== "/yonetim" && path.startsWith(`${target}/`)) ||
        (item.aliases || []).some((alias) => path === alias || path.startsWith(`${alias}/`));
      if (matches && (!best || target.length > normalizePath(best.item.path).length)) best = { group, item };
    }
  }
  return best;
}
